import {
  DonationCheckoutErrors,
  DonationCheckoutResponse,
  DonationCheckoutValidationOptions,
} from '@frx/frx-services';
import { useState } from 'react';
import {
  useDonateCheckout,
  useEventInfo,
  usePostDoubleTheDonation,
  useValidateDonateCheckout,
} from '@frx/frx-react-context';
import * as Sentry from '@sentry/react';
import { DonationField, PaymentSchedule } from '../constants';
import { blackbaudFormMapper, saveSessionFormData, setPrice } from '../utils';
import { useLowercaseQueryString } from './useLowercaseQueryString';
import { useConfirmationNavigate } from './useConfirmationNavigate';
import { useDonationForm } from './useDonationForm';

export type DonationFormType = Record<DonationField, string | number | boolean>;

const isDonationCheckoutErrors = (
  response: DonationCheckoutResponse
): response is DonationCheckoutErrors => 'errors' in response;

export const useDonationSubmit = () => {
  const [errorMessage, setErrorMessage] = useState({
    title: 'Payment failed',
    message: '',
  });
  const { handleConfirmation } = useConfirmationNavigate();
  const { queryStrings } = useLowercaseQueryString();

  const { form: formConfig, getOtherLevelId } = useDonationForm();
  const { mutate: vaildateCheckout } = useValidateDonateCheckout();
  const { mutate: donateCheckout, isLoading } = useDonateCheckout();
  const { mutate: donateDTD } = usePostDoubleTheDonation();
  const { data: trInfo } = useEventInfo();

  const countryCodeNamePairs = JSON.parse(
    <string>(<unknown>formConfig?.checkoutOptions.countryCodeNamePairs) || '{}'
  );

  const handleFormSubmit = (data: DonationFormType) => {
    if (data.donationAmount === 0) return;
    const formData = {
      ...data,
      [DonationField.donationLevelId]: getOtherLevelId(),
      ...(!data.hideMyDisplayName &&
        !(data.donationDisplayName as string)?.length && {
          [DonationField.donationDisplayName]: `${data.firstName} ${data.lastName}`,
        }),
    };

    Sentry.startSpanManual(
      {
        name: 'donation-form-submit',
        op: 'checkout',
        attributes: {
          donationAmount: formData.donationAmount,
          donorEmail: formData.donorEmail,
          donationLevelId: formData.donationLevelId,
        },
        forceTransaction: true,
      },
      (span) => {
        saveSessionFormData(formData as any);

        const blackbaudOptions: DonationCheckoutValidationOptions =
          blackbaudFormMapper(formData, queryStrings);

        Sentry.startSpanManual(
          { name: 'validate-checkout', op: 'checkout', parentSpan: span },
          (validateSpan) =>
            vaildateCheckout(blackbaudOptions, {
              onSuccess: (validateCheckOutRes) => {
                if (validateCheckOutRes.isValid) {
                  // loads the checkout form in the background, but doesn't display it
                  const config = {
                    environmentId: formConfig?.checkoutOptions?.environmentId,
                    paymentConfigurationId:
                      formConfig?.checkoutOptions
                        ?.merchantAccountConfigurationId,
                    applicationName:
                      formConfig?.checkoutOptions.applicationName,
                    paymentMethodOptions: {
                      directDebit: {
                        enabled: formConfig?.checkoutOptions.paymentTypes.debit,
                      },
                      card: {
                        enabled:
                          formConfig?.checkoutOptions.paymentTypes.credit,
                      },
                      payPal: {
                        enabled:
                          formConfig?.checkoutOptions.paymentTypes.paypal,
                      },
                      wallets: {
                        applePayEnabled:
                          formConfig?.checkoutOptions.paymentTypes
                            .digitalWallet,
                        googlePayEnabled:
                          formConfig?.checkoutOptions.paymentTypes
                            .digitalWallet,
                      },
                    },
                    primaryColor: formConfig?.checkoutOptions.primaryColor,
                    workflowMode: 'modal',
                  };
                  const checkoutService = (window as any).BlackbaudCheckout(
                    config
                  );

                  const modalData = {
                    baseAmount: Math.round(
                      Number(formData.donationAmount) * 100
                    ),
                    isRecurring:
                      formData.paymentSchedule === PaymentSchedule.Monthly,
                    transactionCategory: 'donation',
                    recurrenceDescription: '',
                    billingDetails: {
                      email: formData.donorEmail,
                      name: formData.donationDisplayName,
                      address: {
                        postalCode: formData.postCode,
                        country: Object.entries(countryCodeNamePairs).find(
                          ([, val]) =>
                            (val as string)?.toLowerCase() ===
                            (formData.country as string)?.toLowerCase()
                        )?.[0],
                      },
                    },
                  };
                  Sentry.startSpan(
                    {
                      name: 'checkout-modal-open',
                      op: 'checkout',
                      parentSpan: span,
                      attributes: {
                        modalData: JSON.stringify(modalData),
                      },
                    },
                    () => {
                      checkoutService.checkoutModal.open(modalData);
                    }
                  );

                  return checkoutService.checkoutComplete.subscribe(
                    (e: any) => {
                      Sentry.startSpan(
                        {
                          name: 'checkout-complete-handled',
                          op: 'checkout',
                          parentSpan: span,
                          attributes: {
                            transaction: JSON.stringify(e.transaction),
                          },
                        },
                        () => {
                          if (e.transaction?.id) {
                            const donateCheckoutOptions = {
                              ...blackbaudOptions,
                              transactiontoken: e.transaction.id,
                            };
                            Sentry.startSpanManual(
                              {
                                name: 'donate-checkout',
                                op: 'checkout',
                                parentSpan: span,
                                attributes: {
                                  formData: JSON.stringify(
                                    donateCheckoutOptions
                                  ),
                                },
                              },
                              (donateCheckoutSpan) =>
                                donateCheckout(donateCheckoutOptions, {
                                  onSuccess: (
                                    donateCheckOutRes: DonationCheckoutResponse
                                  ) => {
                                    if (
                                      isDonationCheckoutErrors(
                                        donateCheckOutRes
                                      )
                                    ) {
                                      span.end();
                                      return setErrorMessage({
                                        title: `Payment failed: ${donateCheckOutRes?.errors?.reason}`,
                                        message: `${donateCheckOutRes?.errors?.message}`,
                                      });
                                    }
                                    if (
                                      donateCheckOutRes.donation.transaction_id
                                    ) {
                                      const parsedDtdId =
                                        blackbaudOptions?.double_the_donation_company_id;
                                      // DTD should be registered for all p2p donations
                                      Sentry.startSpanManual(
                                        {
                                          name: 'dtd-donation',
                                          op: 'checkout',
                                          parentSpan: span,
                                        },
                                        (dtdSpan) =>
                                          donateDTD(
                                            {
                                              anonymous:
                                                formData?.hideMyDisplayName ||
                                                false,
                                              campaign:
                                                trInfo?.public_event_type_name,
                                              donation_amount: setPrice() || 0,
                                              donation_datetime:
                                                new Date().toString(),
                                              donation_identifier: String(
                                                donateCheckOutRes.donation
                                                  .transaction_id
                                              ),
                                              donor_address: {
                                                address1:
                                                  formData?.address1 || '',
                                                address2:
                                                  formData?.address2 || '',
                                                city: formData?.city || '',
                                                state: formData?.state || '',
                                                zip: formData?.postCode || '',
                                                country:
                                                  formData?.country || '',
                                              },
                                              donor_email:
                                                formData?.donorEmail || '',
                                              donor_first_name:
                                                formData?.firstName || '',
                                              donor_last_name:
                                                formData?.lastName || '',
                                              donor_phone: '',
                                              ...(parsedDtdId
                                                ? {
                                                    doublethedonation_company_id:
                                                      Number(parsedDtdId),
                                                  }
                                                : ({} as any)),
                                              doublethedonation_entered_text:
                                                '',
                                              partner_identifier: '',
                                              recurring:
                                                formData?.paymentSchedule ===
                                                PaymentSchedule.Monthly,
                                            },
                                            {
                                              onSettled: (dtdData, error) => {
                                                dtdSpan.setAttributes({
                                                  response:
                                                    JSON.stringify(dtdData),
                                                  error: JSON.stringify(error),
                                                });
                                                dtdSpan.end();
                                                span.end();
                                                handleConfirmation();
                                              },
                                            }
                                          )
                                      );
                                    }
                                    return null;
                                  },
                                  onError: (error) =>
                                    Sentry.captureException(error),
                                  onSettled: (dcData, error) => {
                                    donateCheckoutSpan.setAttributes({
                                      response: JSON.stringify(dcData),
                                      error: JSON.stringify(error),
                                    });
                                    donateCheckoutSpan.end();
                                  },
                                })
                            );
                          } else {
                            Sentry.startSpan(
                              {
                                name: 'checkout-complete-no-transaction-id',
                                op: 'checkout',
                                parentSpan: span,
                                attributes: {
                                  transactionData: JSON.stringify(e),
                                },
                              },
                              () => {}
                            );
                            span.end();
                          }
                        }
                      );
                    }
                  );
                }
                Sentry.captureMessage(
                  `Form Validation Failed: ${validateCheckOutRes?.errorMessage}`,
                  'error'
                );
                span.end();
                return setErrorMessage({
                  title: 'Form Validation Failed',
                  message: `${validateCheckOutRes?.errorMessage}`,
                });
              },
              onError: (error) => {
                Sentry.captureException(error, {
                  extra: {
                    formData,
                    blackbaudOptions,
                  },
                });
              },
              onSettled: () => validateSpan.end(),
            })
        );
      }
    );
  };

  return {
    isLoading,
    errorMessage,
    handleFormSubmit,
  };
};
